import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Mic, X, Volume2 } from "lucide-react";
import api from "../api";
import { getToken } from "../utils/tokenStorage";
import { createVoiceSession } from "./voiceSession";

/*
  Onboarding voice guide.

  Watches what the person is actually doing — route, idle time, which field is
  focused and still empty, visible errors, failed submits — infers which step of
  the funnel they're on, and speaks the next instruction. If they stall, it says
  something more specific instead of repeating itself.

  Design notes:
  - Hints are SCRIPTED server-side for the known steps: instant, identical every
    time, and impossible to hallucinate. The model is only used for spoken
    questions.
  - It speaks at most once per step unless the person is genuinely stuck, and a
    dismissal is remembered. A guide that talks over you is worse than none.
  - Everything is best-effort: if the mic, speech synthesis or the network is
    unavailable, the widget degrades to silent text and never blocks the UI.
*/

const DISMISS_KEY = "aegis_guide_dismissed";
const DONE_KEY = "aegis_guide_completed";
const IDLE_TICK_MS = 4000;      // how often we re-evaluate
const MIN_GAP_MS = 12000;       // never speak twice inside this window

/* Route -> funnel step. Kept dumb on purpose; DOM state refines it below. */
function stepForPath(pathname, hasToken) {
  const p = (pathname || "").toLowerCase();
  if (p.startsWith("/register")) return "signup";
  if (p.startsWith("/login")) return hasToken ? "dashboard" : "signup";
  if (p.startsWith("/scan")) return "target";
  if (p.startsWith("/dashboard")) return "dashboard";
  return hasToken ? "dashboard" : "signup";
}

/* Pull the visible error text, if any — this is how we know a submit failed. */
function visibleError() {
  const sels = ['[role="alert"]', ".error", "[data-error]", ".au-error"];
  for (const s of sels) {
    const el = document.querySelector(s);
    const t = (el && el.textContent || "").trim();
    if (t && t.length < 300) return t;
  }
  return null;
}

export default function OnboardingGuide() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const hasToken = !!getToken("aegis_token");

  const [open, setOpen] = useState(false);
  const [state, setState] = useState("idle");   // idle | listening | thinking | speaking
  const [line, setLine] = useState("");
  const [heard, setHeard] = useState("");
  const [dismissed, setDismissed] = useState(
    () => { try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; } });

  // Behaviour signals, kept in refs so the tick loop never re-renders on input.
  const lastActivity = useRef(Date.now());
  const stepEnteredAt = useRef(Date.now());
  const focusedField = useRef(null);
  const focusedEmptyAt = useRef(0);
  const submitAttempts = useRef(0);
  const spoken = useRef([]);
  const lastSpokeAt = useRef(0);
  const stepRef = useRef(stepForPath(pathname, hasToken));
  const agentBuf = useRef("");

  // Voice plumbing. The realtime session is opened on demand (tapping the blob);
  // proactive nudges use browser speech so idle page views cost nothing.
  const sessionRef = useRef(null);
  const voiceRef = useRef(null);

  /* ---------- speech ---------- */
  useEffect(() => {
    const load = () => {
      const vs = window.speechSynthesis?.getVoices?.() || [];
      const en = vs.filter((v) => /^en/i.test(v.lang));
      voiceRef.current =
        en.find((v) => /Natural/i.test(v.name)) ||
        en.find((v) => /Google|Samantha|Serena/i.test(v.name)) ||
        en[0] || null;
    };
    load();
    if (window.speechSynthesis) window.speechSynthesis.onvoiceschanged = load;
    return () => { if (window.speechSynthesis) window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  const speak = useCallback((text) => {
    setLine(text);
    lastSpokeAt.current = Date.now();
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      if (voiceRef.current) { u.voice = voiceRef.current; u.lang = voiceRef.current.lang; }
      u.rate = 1.0;
      u.onstart = () => setState("speaking");
      u.onend = () => setState("idle");
      u.onerror = () => setState("idle");
      window.speechSynthesis.speak(u);
    } catch { setState("idle"); }
  }, []);

  /* ---------- actions the guide may take ---------- */
  const runAction = useCallback((action) => {
    if (!action || typeof action !== "object") return;
    if (action.type === "navigate" && action.to && action.to !== pathname) {
      navigate(action.to);
    }
    if (action.type === "highlight" && action.selector) {
      try {
        const el = document.querySelector(action.selector);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("aegis-guide-pulse");
          setTimeout(() => el.classList.remove("aegis-guide-pulse"), 3200);
        }
      } catch { /* selector may not match on this page */ }
    }
  }, [navigate, pathname]);

  /* ---------- activity + field tracking ---------- */
  useEffect(() => {
    const touch = () => { lastActivity.current = Date.now(); };
    const onFocus = (e) => {
      const el = e.target;
      if (!el || !/input|textarea/i.test(el.tagName)) return;
      focusedField.current = (el.name || el.type || "").toLowerCase();
      focusedEmptyAt.current = el.value ? 0 : Date.now();
    };
    const onBlur = () => { focusedField.current = null; focusedEmptyAt.current = 0; };
    const onInput = (e) => {
      touch();
      if (e.target && e.target.value) focusedEmptyAt.current = 0;
    };
    const onSubmitish = (e) => {
      touch();
      const t = e.target;
      if (t && t.closest && t.closest("button,[type=submit]")) submitAttempts.current += 1;
    };
    window.addEventListener("mousemove", touch, { passive: true });
    window.addEventListener("keydown", touch);
    window.addEventListener("scroll", touch, { passive: true });
    window.addEventListener("click", onSubmitish, true);
    window.addEventListener("focusin", onFocus);
    window.addEventListener("focusout", onBlur);
    window.addEventListener("input", onInput, true);
    return () => {
      window.removeEventListener("mousemove", touch);
      window.removeEventListener("keydown", touch);
      window.removeEventListener("scroll", touch);
      window.removeEventListener("click", onSubmitish, true);
      window.removeEventListener("focusin", onFocus);
      window.removeEventListener("focusout", onBlur);
      window.removeEventListener("input", onInput, true);
    };
  }, []);

  /* Reset per-step counters whenever the route changes. */
  useEffect(() => {
    const next = stepForPath(pathname, hasToken);
    if (next !== stepRef.current) {
      stepRef.current = next;
      stepEnteredAt.current = Date.now();
      submitAttempts.current = 0;
    }
  }, [pathname, hasToken]);

  /* ---------- ask the server what to say ---------- */
  const askHint = useCallback(async (question) => {
    const now = Date.now();
    const err = visibleError();
    const body = {
      step: stepRef.current,
      path: pathname,
      idle_ms: now - lastActivity.current,
      elapsed_ms: now - stepEnteredAt.current,
      field_focused: focusedField.current,
      field_empty_ms: focusedEmptyAt.current ? now - focusedEmptyAt.current : 0,
      last_error: err,
      target_invalid: !!(err && /valid|format|domain/i.test(err)),
      target_blocked: !!(err && /not allowed|blocked|authoriz/i.test(err)),
      submit_attempts: submitAttempts.current,
      first_visit: true,
      spoken: spoken.current,
      question: question || null,
    };
    try {
      const { data } = await api.post("/api/onboarding/hint", body);
      return data || null;
    } catch { return null; }
  }, [pathname]);

  const maybeSpeak = useCallback(async () => {
    if (dismissed || state === "listening" || state === "thinking") return;
    if (Date.now() - lastSpokeAt.current < MIN_GAP_MS) return;
    const d = await askHint(null);
    if (!d || !d.say) return;
    if (d.key) {
      if (spoken.current.includes(d.key) && d.reason == null) return;
      if (!spoken.current.includes(d.key)) spoken.current.push(d.key);
    }
    setOpen(true);
    speak(d.say);
    if (d.action) runAction(d.action);
  }, [askHint, dismissed, runAction, speak, state]);

  /* Tick loop — the only thing that decides to speak proactively. */
  useEffect(() => {
    if (dismissed) return undefined;
    try { if (localStorage.getItem(DONE_KEY) === "1") return undefined; } catch { /* ignore */ }
    const id = setInterval(maybeSpeak, IDLE_TICK_MS);
    const first = setTimeout(maybeSpeak, 1500);   // greet shortly after arrival
    return () => { clearInterval(id); clearTimeout(first); };
  }, [maybeSpeak, dismissed]);

  /* ---------- realtime voice session ---------- */
  const endSession = useCallback(() => {
    if (sessionRef.current) { sessionRef.current.stop(); sessionRef.current = null; }
    setState("idle");
  }, []);

  const startSession = useCallback(async () => {
    if (sessionRef.current) { endSession(); return; }
    try { window.speechSynthesis.cancel(); } catch { /* noop */ }
    setState("thinking");
    setLine("Connecting…");
    const sess = createVoiceSession({
      step: stepRef.current,
      path: pathname,
      onEvent: (e) => {
        if (e.type === "ready") { setState("listening"); setLine("I'm listening — just talk."); }
        else if (e.type === "user" && e.text) setHeard((p) => (p + " " + e.text).trim().slice(-160));
        else if (e.type === "agent" && e.text) {
          setState("speaking");
          setLine((p) => (agentBuf.current += e.text, agentBuf.current.trim().slice(-240)));
        }
        else if (e.type === "interrupted") { agentBuf.current = ""; setState("listening"); }
        else if (e.type === "action") runAction(e.action);
        else if (e.type === "error") { setLine(e.message || "Voice unavailable."); endSession(); }
        else if (e.type === "closed") endSession();
      },
    });
    sessionRef.current = sess;
    try {
      await sess.start();
    } catch {
      setLine("I need microphone access to talk. You can still follow the written steps.");
      endSession();
    }
  }, [endSession, pathname, runAction]);

  // Tear the session down on unmount so a live mic never outlives the widget.
  useEffect(() => () => { if (sessionRef.current) sessionRef.current.stop(); }, []);

  const onOrbClick = () => {
    setOpen(true);
    if (sessionRef.current) { endSession(); return; }
    agentBuf.current = "";
    setHeard("");
    startSession();
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    if (sessionRef.current) { sessionRef.current.stop(); sessionRef.current = null; }
    setDismissed(true);
    setOpen(false);
  };

  if (dismissed) return null;

  const status = { idle: "Ready", listening: "Listening", thinking: "Thinking", speaking: "Speaking" }[state];

  return (
    <>
      <style>{CSS}</style>
      <div className="ag-root">
        {open && (
          <div className="ag-panel" role="dialog" aria-label="Onboarding guide">
            <button className="ag-close" onClick={dismiss} aria-label="Dismiss guide"><X size={14} /></button>
            <div className="ag-head">
              <Volume2 size={13} />
              <span className="ag-status">{status}</span>
            </div>
            {heard && <p className="ag-heard">“{heard}”</p>}
            <p className="ag-line">{line || "I'll guide you through your first scan."}</p>
            <button className="ag-ask" onClick={onOrbClick}>
              {sessionRef.current ? "End conversation" : "Talk to the guide"}
            </button>
          </div>
        )}
        <button
          className={`ag-orb ${state}`}
          onClick={onOrbClick}
          aria-label="Onboarding guide"
          title="Onboarding guide"
        >
          <span className="ag-blob">
            <svg viewBox="0 0 100 100" aria-hidden="true">
              <defs>
                <mask id="ag-clip">
                  <polygon points="0,0 100,0 100,100 0,100" fill="black" />
                  <polygon points="25,25 75,25 50,75" fill="white" />
                  <polygon points="50,25 75,75 25,75" fill="white" />
                  <polygon points="35,35 65,35 50,65" fill="white" />
                  <polygon points="35,35 65,35 50,65" fill="white" />
                  <polygon points="35,35 65,35 50,65" fill="white" />
                  <polygon points="35,35 65,35 50,65" fill="white" />
                </mask>
              </defs>
            </svg>
            <span className="ag-box" />
          </span>
          <Mic className="ag-mic" size={17} strokeWidth={2.4} />
        </button>
      </div>
    </>
  );
}

/* Blob visual follows the same construction as the marketing-site widget:
   an SVG polygon mask under a high-contrast filter, with the polygons rotating
   at different origins, which reads as a soft morphing droplet. */
const CSS = `
.ag-root{position:fixed;bottom:20px;right:20px;z-index:70;display:flex;flex-direction:column;align-items:flex-end;gap:10px}
.ag-orb{position:relative;width:66px;height:66px;border:0;background:transparent;cursor:pointer;display:grid;place-items:center}
.ag-orb:focus-visible{outline:2px solid var(--au-accent,#1FD0A3);outline-offset:6px;border-radius:50%}
.ag-blob{--t:1.15s;position:relative;width:52px;height:52px;border-radius:50%;
  box-shadow:0 0 22px rgba(31,208,163,.28),0 12px 30px rgba(31,208,163,.35);
  animation:ag-colorize 3.8s ease-in-out infinite}
.ag-blob svg{position:absolute;width:52px;height:52px}
.ag-box{display:block;width:52px;height:52px;
  background:linear-gradient(180deg,#fff 24%,#1FD0A3 72%);
  mask:url(#ag-clip);-webkit-mask:url(#ag-clip)}
#ag-clip{filter:contrast(15);animation:ag-round calc(var(--t)/2) linear infinite}
#ag-clip polygon{filter:blur(5px)}
#ag-clip polygon:nth-child(2){transform-origin:50% 50%;animation:ag-rot var(--t) linear infinite reverse}
#ag-clip polygon:nth-child(3){transform-origin:50% 60%;animation:ag-rot var(--t) linear infinite;animation-delay:calc(var(--t)/-3)}
#ag-clip polygon:nth-child(4){transform-origin:40% 40%;animation:ag-rot var(--t) linear infinite reverse}
#ag-clip polygon:nth-child(5){transform-origin:40% 40%;animation:ag-rot var(--t) linear infinite reverse;animation-delay:calc(var(--t)/-2)}
#ag-clip polygon:nth-child(6){transform-origin:60% 40%;animation:ag-rot var(--t) linear infinite}
.ag-orb.listening .ag-blob{--t:.55s}
.ag-orb.thinking  .ag-blob{--t:.8s}
.ag-orb.speaking  .ag-blob{--t:.75s;animation:ag-pulse .55s ease-in-out infinite alternate}
.ag-mic{position:absolute;color:#0b3b31;z-index:2}
.ag-panel{position:relative;width:290px;max-width:calc(100vw - 40px);
  background:var(--au-card,#0d1117);border:1px solid var(--au-border,rgba(255,255,255,.12));
  border-radius:16px;padding:14px 16px 13px;box-shadow:0 20px 60px rgba(0,0,0,.45)}
.ag-close{position:absolute;top:8px;right:8px;border:0;background:transparent;cursor:pointer;color:var(--au-muted,#8b98a5);padding:2px}
.ag-head{display:flex;align-items:center;gap:6px;color:var(--au-accent-ink,#1FD0A3)}
.ag-status{font-size:10px;font-weight:700;letter-spacing:.16em;text-transform:uppercase}
.ag-heard{margin:8px 0 0;font-size:12px;font-style:italic;color:var(--au-muted,#8b98a5)}
.ag-line{margin:7px 0 0;font-size:13.5px;line-height:1.55;color:var(--au-text,#e6ede9)}
.ag-ask{margin-top:11px;width:100%;border:1px solid var(--au-border,rgba(255,255,255,.14));
  background:var(--au-glass,rgba(255,255,255,.06));color:var(--au-text-2,#c8d3cc);
  border-radius:999px;padding:7px 12px;font-size:12px;font-weight:600;cursor:pointer}
.ag-ask:hover{background:var(--au-glass-strong,rgba(255,255,255,.12))}
.aegis-guide-pulse{animation:ag-hl 1.1s ease-in-out 3;border-radius:10px}
@keyframes ag-rot{to{transform:rotate(360deg)}}
@keyframes ag-round{0%,60%,100%{filter:contrast(15)}20%,40%{filter:contrast(3)}}
@keyframes ag-colorize{0%,100%{filter:hue-rotate(0deg)}50%{filter:hue-rotate(-20deg)}}
@keyframes ag-pulse{to{transform:scale(1.07)}}
@keyframes ag-hl{0%,100%{box-shadow:0 0 0 0 rgba(31,208,163,0)}50%{box-shadow:0 0 0 4px rgba(31,208,163,.45)}}
@media (prefers-reduced-motion:reduce){.ag-blob,#ag-clip,#ag-clip polygon{animation:none}}
`;
